/**
 * The tilemap editor camera and coordinate transforms (_docs/tilemap/02 §5).
 *
 * Three spaces:
 *   - cell   (integer tile coords, y-up — the engine convention; stored data)
 *   - world  (cells × cellSize, y-up)
 *   - screen (CSS pixels, y-down — pointer events + canvas drawing)
 *
 * The Y flip lives entirely here so stored/exported data is never flipped. No
 * rotation in v1, so screen→world is a straight affine invert (the 2D
 * simplification of the engine's ray-cast paint math, engine 46.1).
 */

export interface Camera {
    /** World-space position at the screen origin's mapping (pan), y-up. */
    x: number;
    y: number;
    /** Screen pixels per world unit. */
    zoom: number;
}

export const MIN_ZOOM = 2;
export const MAX_ZOOM = 256;

export function clampZoom(zoom: number): number {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/**
 * World → screen. World is y-up; screen is y-down, so world Y is subtracted
 * from the canvas height. `cam.x/cam.y` is the world point shown at the
 * bottom-left of the viewport.
 */
export function worldToScreen(
    cam: Camera,
    canvasH: number,
    wx: number,
    wy: number,
): { x: number; y: number } {
    return {
        x: (wx - cam.x) * cam.zoom,
        y: canvasH - (wy - cam.y) * cam.zoom,
    };
}

/** Screen → world (inverse of worldToScreen). */
export function screenToWorld(
    cam: Camera,
    canvasH: number,
    sx: number,
    sy: number,
): { x: number; y: number } {
    return {
        x: sx / cam.zoom + cam.x,
        y: (canvasH - sy) / cam.zoom + cam.y,
    };
}

/** Screen pixel → integer cell (floor of world / cellSize). */
export function screenToCell(
    cam: Camera,
    canvasH: number,
    cellSize: number,
    sx: number,
    sy: number,
): { x: number; y: number } {
    const w = screenToWorld(cam, canvasH, sx, sy);
    return {
        x: Math.floor(w.x / cellSize),
        y: Math.floor(w.y / cellSize),
    };
}

/**
 * Zoom toward a screen anchor (wheel/pinch): keep the world point under the
 * cursor fixed while scaling. Returns a new camera.
 */
export function zoomAt(
    cam: Camera,
    canvasH: number,
    anchorSx: number,
    anchorSy: number,
    factor: number,
): Camera {
    const before = screenToWorld(cam, canvasH, anchorSx, anchorSy);
    const zoom = clampZoom(cam.zoom * factor);
    const after = { ...cam, zoom };
    const afterWorld = screenToWorld(after, canvasH, anchorSx, anchorSy);
    // Shift the camera so `before` maps back under the cursor.
    return {
        zoom,
        x: after.x + (before.x - afterWorld.x),
        y: after.y + (before.y - afterWorld.y),
    };
}

/** Pan by a screen-space delta (drag). */
export function panByScreen(cam: Camera, dsx: number, dsy: number): Camera {
    return {
        ...cam,
        x: cam.x - dsx / cam.zoom,
        y: cam.y + dsy / cam.zoom, // screen y-down → world y-up
    };
}

/**
 * The inclusive range of chunk coordinates currently visible, given the
 * viewport size in CSS pixels. Used to cull rendering and to range-fetch chunk
 * data. `margin` widens the box (in chunks) for prefetch.
 */
export function visibleChunkRange(
    cam: Camera,
    canvasW: number,
    canvasH: number,
    cellSize: number,
    chunkSize: number,
    margin = 0,
): { cx0: number; cy0: number; cx1: number; cy1: number } {
    const bl = screenToWorld(cam, canvasH, 0, canvasH); // bottom-left
    const tr = screenToWorld(cam, canvasH, canvasW, 0); // top-right
    const chunkWorld = cellSize * chunkSize;
    return {
        cx0: Math.floor(bl.x / chunkWorld) - margin,
        cy0: Math.floor(bl.y / chunkWorld) - margin,
        cx1: Math.floor(tr.x / chunkWorld) + margin,
        cy1: Math.floor(tr.y / chunkWorld) + margin,
    };
}
